import {
  Snackbar,
  Alert,
  CircularProgress,
  Backdrop,
  Icon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  useMediaQuery,
  useTheme,
  Card,
  Chip,
  Divider,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
} from "@mui/material";

import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import MDButton from "theme/components/MDButton";
import DataTable from "theme/Tables/DataTable";

import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";

import axios from "axios.js";
import usePageTitle from "app/pageTitle";
import { useAwaitDialog } from "app/components/status_dialog";
import AppConfig from "appConfig";
import { useAuthUser } from "react-auth-kit";
import * as XLSX from "xlsx";

function CallCenterContactsPage() {
  const pageTitle = "Contact List";
  usePageTitle(pageTitle);

  const auth = useAuthUser();
  const accountInfo = auth();

  const navigate = useNavigate();
  const { ref } = useParams();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const statusDialog = useAwaitDialog();

  const [showLoading, setShowLoading] = useState(false);

  const [open, setOpen] = useState(false);

  const [errorMsg, setErrMsg] = useState("");

  const [severity, setSeverity] = useState("");

  const [refreshPage, setRefreshPage] = useState(false);

  const [callCenter, setCallCenter] = useState({});

  const [contacts, setContacts] = useState([]);

  const [tags, setTags] = useState([]);

  const [uploadFile, setUploadFile] = useState(null);

  const [selectedTag, setSelectedTag] = useState(null);

  const [openUploadContactsDialog, setOpenUploadContactsDialog] = useState(false);

  const [search, setSearch] = useState("");

  /*
   * Edit contact
   */
  const [openEdit, setOpenEdit] = useState(false);

  const [editContact, setEditContact] = useState({
    reference: "",
    name: "",
    phone_number: "",
    tag: null,
    attributes: [],
  });

  /*
   * Add contact
   *
   * Attributes are deliberately kept as
   * an array of key/value pairs.
   *
   * This means every contact can have
   * completely different attributes.
   */
  const [openAddContact, setOpenAddContact] = useState(false);

  const [newContact, setNewContact] = useState({
    name: "",
    phone_number: "",
    tag: "",
    attributes: [],
  });

  /*
   * Selected contacts
   */
  const [selectedContacts, setSelectedContacts] = useState([]);

  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  /*
   * Load contacts
   */
  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);

      await axios
        .get(`/portal/voice/call_centers/${ref}/contacts.json`)
        .then((result) => {
          setShowLoading(false);

          if (result.data.status === "success") {
            setCallCenter(result.data.data.call_center || {});

            setContacts(result.data.data.contacts || []);

            setTags(result.data.data.tags || []);
          } else {
            setSeverity("error");
            setErrMsg(result.data.data.message);
            setOpen(true);
          }
        })
        .catch(() => {
          setShowLoading(false);

          setSeverity("error");

          setErrMsg(
            `Unable to complete request. ${
              navigator.onLine ? "" : "Make sure you have an active internet connection"
            }`
          );

          setOpen(true);
        });
    };

    fetchData();
  }, [ref, refreshPage]);

  /*
   * Filter contacts
   */
  const filteredContacts = useMemo(() => {
    if (!search) {
      return contacts;
    }

    const q = search.toLowerCase();

    return contacts.filter(
      (row) =>
        row.name?.toLowerCase().includes(q) ||
        row.phone_number?.toLowerCase().includes(q) ||
        row.tag?.title?.toLowerCase().includes(q)
    );
  }, [contacts, search]);

  /*
   * Dynamic attribute columns.
   *
   * These are only used for displaying
   * attributes that exist in the contact list.
   *
   * They are NOT used to build the Add
   * Contact form.
   */
  const attributeHeaders = useMemo(() => {
    const headers = new Set();

    contacts.forEach((contact) => {
      if (
        contact.attributes &&
        typeof contact.attributes === "object" &&
        !Array.isArray(contact.attributes)
      ) {
        Object.keys(contact.attributes).forEach((key) => {
          headers.add(key);
        });
      }
    });

    return Array.from(headers);
  }, [contacts]);

  /*
   * Format attribute name
   */
  const formatAttributeName = (name) => {
    return String(name)
      .replace(/_/g, " ")
      .replace(/\b\w/g, (letter) => letter.toUpperCase());
  };

  /*
   * Format attribute value
   */
  const formatAttributeValue = (value) => {
    if (value === null || value === undefined || value === "") {
      return "---";
    }

    if (typeof value === "object") {
      return JSON.stringify(value);
    }

    return String(value);
  };

  /*
   * Filtered references
   */
  const filteredContactReferences = useMemo(
    () => filteredContacts.map((contact) => contact.reference),
    [filteredContacts]
  );

  /*
   * All selected
   */
  const allFilteredSelected =
    filteredContactReferences.length > 0 &&
    filteredContactReferences.every((reference) => selectedContacts.includes(reference));

  /*
   * Some selected
   */
  const someFilteredSelected =
    filteredContactReferences.some((reference) => selectedContacts.includes(reference)) &&
    !allFilteredSelected;

  /*
   * Toggle contact selection
   */
  const toggleContactSelection = (reference) => {
    setSelectedContacts((current) => {
      if (current.includes(reference)) {
        return current.filter((item) => item !== reference);
      }

      return [...current, reference];
    });
  };

  /*
   * Select all
   */
  const toggleSelectAll = () => {
    setSelectedContacts((current) => {
      if (allFilteredSelected) {
        return current.filter((reference) => !filteredContactReferences.includes(reference));
      }

      const merged = [...current];

      filteredContactReferences.forEach((reference) => {
        if (!merged.includes(reference)) {
          merged.push(reference);
        }
      });

      return merged;
    });
  };

  /*
   * ========================================
   * ADD CONTACT
   * ========================================
   */

  /*
   * Open Add Contact dialog
   */
  const openAddContactDialog = () => {
    setNewContact({
      name: "",
      phone_number: "",
      tag: "",
      attributes: [],
    });

    setOpenAddContact(true);
  };

  /*
   * Close Add Contact dialog
   */
  const closeAddContactDialog = () => {
    setOpenAddContact(false);

    setNewContact({
      name: "",
      phone_number: "",
      tag: "",
      attributes: [],
    });
  };

  /*
   * Add a completely new attribute.
   */
  const addContactAttribute = () => {
    setNewContact((current) => ({
      ...current,
      attributes: [
        ...current.attributes,
        {
          key: "",
          value: "",
        },
      ],
    }));
  };

  /*
   * Update an attribute.
   */
  const updateContactAttribute = (index, field, value) => {
    setNewContact((current) => {
      const attributes = [...current.attributes];

      attributes[index] = {
        ...attributes[index],
        [field]: value,
      };

      return {
        ...current,
        attributes,
      };
    });
  };

  /*
   * Remove an attribute.
   */
  const removeContactAttribute = (index) => {
    setNewContact((current) => ({
      ...current,
      attributes: current.attributes.filter((_, attributeIndex) => attributeIndex !== index),
    }));
  };

  /*
   * Save new contact
   */
  const saveNewContact = async () => {
    if (!newContact.name || !newContact.name.trim()) {
      setSeverity("error");
      setErrMsg("Name is required.");
      setOpen(true);
      return;
    }

    if (!newContact.phone_number || !newContact.phone_number.trim()) {
      setSeverity("error");
      setErrMsg("Phone number is required.");
      setOpen(true);
      return;
    }

    /*
     * Convert the key/value array
     * into the JSON object expected
     * by the backend.
     */
    const attributes = {};

    for (let index = 0; index < newContact.attributes.length; index++) {
      const attribute = newContact.attributes[index];

      const key = attribute.key?.trim();

      const value = attribute.value;

      /*
       * Ignore completely empty rows.
       */
      if (!key && !value) {
        continue;
      }

      /*
       * Attribute name is required
       * when a value has been entered.
       */
      if (!key) {
        setSeverity("error");
        setErrMsg(`Please enter an attribute name for attribute ${index + 1}.`);
        setOpen(true);
        return;
      }

      /*
       * Prevent duplicate keys.
       */
      if (Object.prototype.hasOwnProperty.call(attributes, key)) {
        setSeverity("error");
        setErrMsg(`The attribute "${key}" has been added more than once.`);
        setOpen(true);
        return;
      }

      attributes[key] = value;
    }

    const payload = {
      reference: ref,
      name: newContact.name.trim(),
      phone_number: newContact.phone_number.trim(),
      tag: newContact.tag || null,
      attributes,
    };

    closeAddContactDialog();
    setShowLoading(true);

    await axios
      .post("/portal/voice/call_centers/contacts/create.json", payload)
      .then(async (result) => {
        setShowLoading(false);

        if (result.data.status === "success") {
          closeAddContactDialog();

          await statusDialog.show(result.data.data.message, "success", "Success!");

          setRefreshPage(!refreshPage);
        } else {
          await statusDialog.show(result.data.data.message, "error", "Error!");
        }
      })
      .catch(async () => {
        setShowLoading(false);

        await statusDialog.show(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`,
          "error",
          "Error!"
        );
      });
  };

  /*
   * ========================================
   * EDIT CONTACT
   * ========================================
   */

  /*
   * Open edit dialog.
   *
   * Convert the contact's attributes object
   * into an array so it can be edited using
   * the same key/value UI used by Add Contact.
   */
  const openEditDialog = (row) => {
    const attributes = [];

    if (row.attributes && typeof row.attributes === "object" && !Array.isArray(row.attributes)) {
      Object.entries(row.attributes).forEach(([key, value]) => {
        attributes.push({
          key,
          value:
            value === null || value === undefined
              ? ""
              : typeof value === "object"
              ? JSON.stringify(value)
              : String(value),
        });
      });
    }

    setEditContact({
      reference: row.reference,
      name: row.name,
      phone_number: row.phone_number,
      tag: row.tag || null,
      attributes,
    });

    setOpenEdit(true);
  };

  /*
   * Add an edit attribute.
   */
  const addEditContactAttribute = () => {
    setEditContact((current) => ({
      ...current,
      attributes: [
        ...current.attributes,
        {
          key: "",
          value: "",
        },
      ],
    }));
  };

  /*
   * Update an edit attribute.
   */
  const updateEditContactAttribute = (index, field, value) => {
    setEditContact((current) => {
      const attributes = [...current.attributes];

      attributes[index] = {
        ...attributes[index],
        [field]: value,
      };

      return {
        ...current,
        attributes,
      };
    });
  };

  /*
   * Remove an edit attribute.
   */
  const removeEditContactAttribute = (index) => {
    setEditContact((current) => ({
      ...current,
      attributes: current.attributes.filter((_, attributeIndex) => attributeIndex !== index),
    }));
  };

  /*
   * Save edited contact.
   */
  const saveContact = async () => {
    if (!editContact.name || !editContact.name.trim()) {
      setSeverity("error");
      setErrMsg("Name is required.");
      setOpen(true);
      return;
    }

    if (!editContact.phone_number || !editContact.phone_number.trim()) {
      setSeverity("error");
      setErrMsg("Phone number is required.");
      setOpen(true);
      return;
    }

    /*
     * Convert the key/value array
     * into the JSON object expected
     * by the backend.
     */
    const attributes = {};

    for (let index = 0; index < editContact.attributes.length; index++) {
      const attribute = editContact.attributes[index];

      const key = attribute.key?.trim();

      const value = attribute.value;

      /*
       * Ignore completely empty rows.
       */
      if (!key && !value) {
        continue;
      }

      /*
       * Attribute name is required
       * when a value has been entered.
       */
      if (!key) {
        setSeverity("error");
        setErrMsg(`Please enter an attribute name for attribute ${index + 1}.`);
        setOpen(true);
        return;
      }

      /*
       * Prevent duplicate keys.
       */
      if (Object.prototype.hasOwnProperty.call(attributes, key)) {
        setSeverity("error");
        setErrMsg(`The attribute "${key}" has been added more than once.`);
        setOpen(true);
        return;
      }

      attributes[key] = value;
    }

    const json = {
      reference: editContact.reference,
      name: editContact.name.trim(),
      phone_number: editContact.phone_number.trim(),
      tag: editContact.tag?.reference || null,
      attributes,
    };

    setShowLoading(true);
    setOpenEdit(false);

    await axios
      .post("/portal/voice/call_centers/contacts/edit.json", json)
      .then(async (result) => {
        setShowLoading(false);

        if (result.data.status === "success") {
          await statusDialog.show(result.data.data.message, "success", "Success!");

          setRefreshPage(!refreshPage);
        } else {
          await statusDialog.show(result.data.data.message, "error", "Error!");
        }
      })
      .catch(async () => {
        setShowLoading(false);

        await statusDialog.show(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`,
          "error",
          "Error!"
        );
      });
  };

  /*
   * ========================================
   * DELETE
   * ========================================
   */

  const deleteContact = async (reference) => {
    const confirmed = window.confirm("Delete this contact?\n\nThis action cannot be undone.");
    if (!confirmed) {
      return;
    }

    setShowLoading(true);

    await axios
      .get(`/portal/voice/call_centers/contacts/${reference}/delete.json`)
      .then(async (result) => {
        setShowLoading(false);

        if (result.data.status === "success") {
          setSelectedContacts((current) => current.filter((item) => item !== reference));
          await statusDialog.show(result.data.data.message, "success", "Success!");
          setRefreshPage(!refreshPage);
        } else {
          await statusDialog.show(result.data.data.message, "error", "Error!");
        }
      })
      .catch(async () => {
        setShowLoading(false);
        await statusDialog.show(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`,
          "error",
          "Error!"
        );
      });
  };

  const deleteSelectedContacts = async () => {
    if (selectedContacts.length === 0) {
      alert("No contacts selected");
      return;
    }

    const confirmed = window.confirm(
      `Delete ${selectedContacts.length} contact${selectedContacts.length === 1 ? "" : "s"}?`,
      "This action cannot be undone."
    );

    if (!confirmed) {
      return;
    }

    setShowLoading(true);
    const json = {
      contacts: selectedContacts,
    };
    await axios
      .post("/portal/voice/call_centers/contacts/delete.json", json)
      .then(async (result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setSelectedContacts([]);
          await statusDialog.show(result.data.data.message, "success", "Success!");
          setRefreshPage(!refreshPage);
        } else {
          await statusDialog.show(result.data.data.message, "error", "Error!");
        }
      })
      .catch(async () => {
        setShowLoading(false);
        await statusDialog.show(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`,
          "error",
          "Error!"
        );
      });
  };

  const clearSelectedContacts = () => {
    setSelectedContacts([]);
  };

  /*
   * ========================================
   * EXPORT
   * ========================================
   */

  const exportContacts = () => {
    window.open(
      `${AppConfig.api_base_url}portal/voice/call_centers/contacts/${ref}/${accountInfo.user.reference}/export.json`,
      "_blank"
    );
  };

  /*
   * ========================================
   * TABLE
   * ========================================
   */

  const columns = [
    {
      Header: (
        <Checkbox
          size="small"
          checked={allFilteredSelected}
          indeterminate={someFilteredSelected}
          onChange={toggleSelectAll}
          disabled={filteredContacts.length === 0}
        />
      ),
      accessor: "selection",
      disableSorting: true,
      disableResizing: true,
      width: 50,
      Cell: ({ row }) => (
        <Checkbox
          size="small"
          checked={selectedContacts.includes(row.original.reference)}
          onChange={() => toggleContactSelection(row.original.reference)}
        />
      ),
    },

    {
      Header: "Name",
      accessor: "name",
    },

    {
      Header: "Phone Number",
      accessor: "phone_number",
    },

    {
      Header: "Tag",
      accessor: "tag",
      Cell: ({ value }) =>
        value ? (
          <Chip label={value.title} size="small" variant="outlined" />
        ) : (
          <MDTypography variant="caption" color="text">
            ---
          </MDTypography>
        ),
    },

    /*
     * Dynamic attribute columns
     */
    ...attributeHeaders.map((attribute) => ({
      Header: formatAttributeName(attribute),
      accessor: `attributes.${attribute}`,
      Cell: ({ row }) => {
        const value = row.original.attributes?.[attribute];
        return (
          <MDTypography variant="caption" color="text">
            {formatAttributeValue(value)}
          </MDTypography>
        );
      },
    })),

    {
      Header: "Called",
      accessor: "called",
      Cell: ({ value }) => (
        <Chip
          label={value ? "Called" : "Pending"}
          color={value ? "success" : "default"}
          size="small"
        />
      ),
    },

    {
      Header: "Actions",
      accessor: "actions",
      align: "right",
      disableSorting: true,
      Cell: ({ row }) => (
        <MDBox display="flex" alignItems="center" gap={1}>
          <MDButton
            iconOnly
            size="small"
            variant="outlined"
            color="warning"
            onClick={(event) => {
              event.stopPropagation();

              openEditDialog(row.original);
            }}
          >
            <Icon>edit</Icon>
          </MDButton>

          <MDButton
            iconOnly
            size="small"
            variant="outlined"
            color="error"
            onClick={(event) => {
              event.stopPropagation();

              deleteContact(row.original.reference);
            }}
          >
            <Icon>delete</Icon>
          </MDButton>
        </MDBox>
      ),
    },
  ];

  /*
   * ========================================
   * UPLOAD
   * ========================================
   */

  function downloadTemplate(headers, filename = "sample_upload.xlsx") {
    const worksheet = XLSX.utils.aoa_to_sheet([headers]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Template");
    XLSX.writeFile(workbook, filename);
  }

  async function fileSelected(e) {
    const fileTypes = ["xlsx", "xls", "csv"];
    const file = e.target.files[0];
    if (!file) {
      return;
    }

    const extension = file.name.split(".").pop().toLowerCase();
    if (!fileTypes.includes(extension)) {
      setSeverity("error");
      setErrMsg("Only excel and csv files accepted.");
      setOpen(true);
      return;
    }

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, {
        type: "array",
      });

      const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(firstSheet, {
        header: 1,
        defval: "",
      });

      if (!json.length) {
        throw new Error("Empty file");
      }

      const headers = json[0].map((h) => String(h).trim().toLowerCase());
      const requiredColumns = ["name", "phone_number"];
      const missing = requiredColumns.filter((column) => !headers.includes(column));
      if (missing.length > 0) {
        setSeverity("error");
        setErrMsg(`Missing required columns: ${missing.join(", ")}`);
        setOpen(true);
        return;
      }

      setUploadFile(file);
    } catch (err) {
      setSeverity("error");
      setErrMsg("Invalid or unreadable file.");
      setOpen(true);
    }
  }

  async function uploadContacts() {
    if (!uploadFile) {
      return;
    }

    const formData = new FormData();
    formData.append("reference", ref);
    if (selectedTag) {
      formData.append("tag", selectedTag);
    }

    formData.append("file", uploadFile);
    setOpenUploadContactsDialog(false);
    setShowLoading(true);

    await axios
      .post("/portal/voice/call_centers/contacts/upload.json", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then(async (result) => {
        setShowLoading(false);

        if (result.data.status === "success") {
          setUploadFile(null);
          setSelectedTag(null);

          await statusDialog.show(result.data.data.message, "success", "Success!");
          setRefreshPage(!refreshPage);
        } else {
          await statusDialog.show(result.data.data.message, "error", "Action Failed!");
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />

      {/* Loading */}
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
        open={showLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Snackbar */}
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Alert
          onClose={handleClose}
          severity={severity}
          sx={{
            width: "100%",
          }}
        >
          {errorMsg}
        </Alert>
      </Snackbar>

      {/* =====================================
          ADD CONTACT DIALOG
          ===================================== */}
      <Dialog open={openAddContact} onClose={closeAddContactDialog} fullWidth maxWidth="sm">
        <DialogTitle>Add Contact</DialogTitle>

        <DialogContent>
          <MDBox pt={1} display="flex" flexDirection="column" gap={3}>
            <TextField
              label="Name"
              fullWidth
              required
              value={newContact.name}
              onChange={(e) =>
                setNewContact((current) => ({
                  ...current,
                  name: e.target.value,
                }))
              }
            />

            <TextField
              label="Phone Number"
              fullWidth
              required
              value={newContact.phone_number}
              onChange={(e) =>
                setNewContact((current) => ({
                  ...current,
                  phone_number: e.target.value,
                }))
              }
            />

            <FormControl fullWidth>
              <InputLabel>Contact Tag</InputLabel>

              <Select
                label="Contact Tag"
                value={newContact.tag || ""}
                onChange={(e) =>
                  setNewContact((current) => ({
                    ...current,
                    tag: e.target.value,
                  }))
                }
                sx={{
                  padding: "12px",
                  paddingLeft: "3px",
                }}
              >
                <MenuItem value="">No tag</MenuItem>

                {tags.map((tag) => (
                  <MenuItem key={tag.reference} value={tag.reference}>
                    {tag.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Divider />

            {/* CONTACT-SPECIFIC ATTRIBUTES */}
            <MDBox>
              <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <MDBox>
                  <MDTypography variant="h6" fontWeight="medium">
                    Attributes
                  </MDTypography>

                  <MDTypography variant="caption" color="text">
                    Add any information specific to this contact.
                  </MDTypography>
                </MDBox>

                <MDButton
                  size="small"
                  variant="outlined"
                  color="dark"
                  onClick={addContactAttribute}
                  iconOnly
                >
                  <Icon>add</Icon>
                </MDButton>
              </MDBox>

              {newContact.attributes.length === 0 && (
                <MDBox
                  py={3}
                  px={2}
                  textAlign="center"
                  sx={{
                    border: "1px dashed",
                    borderColor: "divider",
                    borderRadius: 1,
                  }}
                >
                  <MDTypography variant="caption" color="text">
                    No attributes added.
                  </MDTypography>

                  <MDBox mt={1}>
                    <MDButton
                      size="small"
                      variant="text"
                      color="dark"
                      startIcon={<Icon>add</Icon>}
                      onClick={addContactAttribute}
                    >
                      Add an attribute
                    </MDButton>
                  </MDBox>
                </MDBox>
              )}

              {newContact.attributes.map((attribute, index) => (
                <MDBox key={index} display="flex" alignItems="center" gap={1} mb={2}>
                  <TextField
                    label="Attribute"
                    placeholder="e.g. gender"
                    value={attribute.key}
                    onChange={(e) => updateContactAttribute(index, "key", e.target.value)}
                    sx={{
                      flex: 1,
                    }}
                  />

                  <TextField
                    label="Value"
                    placeholder="e.g. Male"
                    value={attribute.value}
                    onChange={(e) => updateContactAttribute(index, "value", e.target.value)}
                    sx={{
                      flex: 1,
                    }}
                  />

                  <MDButton
                    iconOnly
                    size="small"
                    color="error"
                    variant="text"
                    onClick={() => removeContactAttribute(index)}
                  >
                    <Icon>delete</Icon>
                  </MDButton>
                </MDBox>
              ))}
            </MDBox>
          </MDBox>
        </DialogContent>

        <DialogActions>
          <MDButton color="dark" variant="outlined" onClick={closeAddContactDialog}>
            Cancel
          </MDButton>

          <MDButton color="dark" variant="contained" onClick={saveNewContact}>
            Add Contact
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* =====================================
          UPLOAD CONTACTS DIALOG
          ===================================== */}
      <Dialog open={openUploadContactsDialog} fullWidth maxWidth="xs">
        <DialogTitle>Upload Contacts</DialogTitle>

        <DialogContent>
          <MDBox mt={1} mb={3}>
            <TextField
              accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
              type="file"
              name="upload_file"
              id="upload_file"
              onChange={fileSelected}
              fullWidth
            />

            <MDBox mt={2}>
              <Alert severity="info">
                <MDTypography variant="subtitle2" fontSize={14} mb={0.5}>
                  Contact attributes
                </MDTypography>

                <MDTypography variant="caption" color="text">
                  Your file must contain <strong>name</strong> and <strong>phone_number</strong>{" "}
                  columns. Any additional columns will be stored as attributes.
                </MDTypography>
              </Alert>
            </MDBox>

            <MDBox mt={2}>
              <Alert severity="warning">
                <MDTypography variant="subtitle2" fontSize={14} mb={1}>
                  Download sample upload template.
                </MDTypography>

                <MDButton
                  size="small"
                  variant="contained"
                  color="dark"
                  onClick={() =>
                    downloadTemplate(["name", "phone_number"], "sample_contact_upload.xlsx")
                  }
                >
                  Sample Template
                </MDButton>
              </Alert>
            </MDBox>

            <MDBox mt={4}>
              <FormControl
                sx={{
                  minWidth: 120,
                  width: "100%",
                }}
              >
                <InputLabel>Contact Tag</InputLabel>

                <Select
                  label="Contact Tag"
                  value={selectedTag || ""}
                  onChange={(e) => setSelectedTag(e.target.value)}
                  sx={{
                    padding: "12px",
                    paddingLeft: "3px",
                  }}
                >
                  <MenuItem value="">No tag</MenuItem>

                  {tags.map((record) => (
                    <MenuItem key={record.reference} value={record.reference}>
                      {record.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </MDBox>
          </MDBox>
        </DialogContent>

        <DialogActions>
          <MDButton
            onClick={() => setOpenUploadContactsDialog(false)}
            color="dark"
            variant="outlined"
          >
            Cancel
          </MDButton>

          <MDButton
            color="dark"
            variant="contained"
            onClick={uploadContacts}
            disabled={!uploadFile}
          >
            Upload
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* =====================================
          EDIT CONTACT DIALOG
          ===================================== */}
      <Dialog open={openEdit} onClose={() => setOpenEdit(false)} fullWidth maxWidth="sm">
        <DialogTitle>Edit Contact</DialogTitle>

        <DialogContent>
          <MDBox pt={1} display="flex" flexDirection="column" gap={3}>
            <TextField
              label="Name"
              fullWidth
              required
              value={editContact.name}
              onChange={(e) =>
                setEditContact((current) => ({
                  ...current,
                  name: e.target.value,
                }))
              }
            />

            <TextField
              label="Phone Number"
              fullWidth
              required
              value={editContact.phone_number}
              onChange={(e) =>
                setEditContact((current) => ({
                  ...current,
                  phone_number: e.target.value,
                }))
              }
            />

            <FormControl fullWidth>
              <InputLabel>Contact Tag</InputLabel>

              <Select
                label="Contact Tag"
                value={editContact.tag?.reference || ""}
                onChange={(e) => {
                  const tag = tags.find((item) => item.reference === e.target.value) || null;

                  setEditContact((current) => ({
                    ...current,
                    tag,
                  }));
                }}
                sx={{
                  padding: "12px",
                  paddingLeft: "3px",
                }}
              >
                <MenuItem value="">No tag</MenuItem>

                {tags.map((tag) => (
                  <MenuItem key={tag.reference} value={tag.reference}>
                    {tag.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Divider />

            {/* CONTACT-SPECIFIC ATTRIBUTES */}
            <MDBox>
              <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <MDBox>
                  <MDTypography variant="h6" fontWeight="medium">
                    Attributes
                  </MDTypography>

                  <MDTypography variant="caption" color="text">
                    Add or update information specific to this contact.
                  </MDTypography>
                </MDBox>

                <MDButton
                  size="small"
                  variant="outlined"
                  color="dark"
                  onClick={addEditContactAttribute}
                  iconOnly
                >
                  <Icon>add</Icon>
                </MDButton>
              </MDBox>

              {editContact.attributes.length === 0 && (
                <MDBox
                  py={3}
                  px={2}
                  textAlign="center"
                  sx={{
                    border: "1px dashed",
                    borderColor: "divider",
                    borderRadius: 1,
                  }}
                >
                  <MDTypography variant="caption" color="text">
                    No attributes added.
                  </MDTypography>

                  <MDBox mt={1}>
                    <MDButton
                      size="small"
                      variant="text"
                      color="dark"
                      startIcon={<Icon>add</Icon>}
                      onClick={addEditContactAttribute}
                    >
                      Add an attribute
                    </MDButton>
                  </MDBox>
                </MDBox>
              )}

              {editContact.attributes.map((attribute, index) => (
                <MDBox key={index} display="flex" alignItems="center" gap={1} mb={2}>
                  <TextField
                    label="Attribute"
                    placeholder="e.g. gender"
                    value={attribute.key}
                    onChange={(e) => updateEditContactAttribute(index, "key", e.target.value)}
                    sx={{
                      flex: 1,
                    }}
                  />

                  <TextField
                    label="Value"
                    placeholder="e.g. Male"
                    value={attribute.value}
                    onChange={(e) => updateEditContactAttribute(index, "value", e.target.value)}
                    sx={{
                      flex: 1,
                    }}
                  />

                  <MDButton
                    iconOnly
                    size="small"
                    color="error"
                    variant="text"
                    onClick={() => removeEditContactAttribute(index)}
                  >
                    <Icon>delete</Icon>
                  </MDButton>
                </MDBox>
              ))}
            </MDBox>
          </MDBox>
        </DialogContent>

        <DialogActions>
          <MDButton onClick={() => setOpenEdit(false)} color="dark" variant="outlined">
            Cancel
          </MDButton>

          <MDButton color="dark" variant="contained" onClick={saveContact}>
            Save
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* =====================================
          PAGE
          ===================================== */}
      <MDBox mt={1} mb={5}>
        {/* Back */}
        <MDBox mb={2}>
          <Button
            color="dark"
            size="small"
            startIcon={<Icon>west</Icon>}
            onClick={() => {
              navigate(`/voice/call-centers/${ref}`);
            }}
          >
            Back
          </Button>
        </MDBox>

        {/* Header */}
        <MDBox mb={1}>
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={2}
            px={1}
          >
            <MDBox>
              <MDBox display="flex" alignItems="center" gap={1}>
                <Icon>contacts</Icon>

                <MDTypography variant="h6" fontWeight="bold">
                  Contact List
                </MDTypography>
              </MDBox>

              <MDTypography variant="button" color="text" fontWeight="light">
                {callCenter.title}
              </MDTypography>
            </MDBox>

            <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
              {selectedContacts.length > 0 && (
                <>
                  <MDTypography
                    variant="button"
                    color="text"
                    fontWeight="medium"
                    sx={{
                      mr: 1,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {selectedContacts.length} selected
                  </MDTypography>

                  <MDButton
                    color="error"
                    variant="contained"
                    size="small"
                    startIcon={<Icon>delete</Icon>}
                    onClick={deleteSelectedContacts}
                  >
                    Delete Selected
                  </MDButton>

                  <MDButton
                    color="dark"
                    variant="outlined"
                    size="small"
                    onClick={clearSelectedContacts}
                  >
                    Clear
                  </MDButton>
                </>
              )}

              <MDButton
                color="dark"
                variant="contained"
                size="small"
                onClick={openAddContactDialog}
                iconOnly
              >
                <Icon>person_add</Icon>
              </MDButton>

              <MDButton
                color="dark"
                variant="outlined"
                size="small"
                iconOnly
                onClick={() => setOpenUploadContactsDialog(true)}
              >
                <Icon>upload</Icon>
              </MDButton>

              <MDButton
                color="dark"
                variant="outlined"
                size="small"
                iconOnly
                onClick={exportContacts}
              >
                <Icon>download</Icon>
              </MDButton>
            </MDBox>
          </MDBox>

          <MDBox mt={2}>
            <Divider />
          </MDBox>
        </MDBox>

        {/* Search */}
        <MDBox mb={3}>
          <Card>
            <MDBox p={2}>
              <MDTypography mb={1} variant="h6">
                Filter Contacts
              </MDTypography>

              <TextField
                fullWidth
                placeholder="Search by name, phone number or tag"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon>search</Icon>
                    </InputAdornment>
                  ),
                }}
              />
            </MDBox>
          </Card>
        </MDBox>

        {/* MOBILE */}
        {isMobile ? (
          <MDBox display="flex" flexDirection="column" gap={2}>
            {filteredContacts.length === 0 && (
              <Card
                sx={{
                  marginTop: 0,
                }}
              >
                <MDBox p={4} textAlign="center">
                  <Icon
                    sx={{
                      fontSize: 48,
                      color: "text.secondary",
                    }}
                  >
                    contacts
                  </Icon>

                  <MDTypography variant="h6" fontWeight="medium" mt={1}>
                    No contacts found
                  </MDTypography>

                  <MDTypography variant="button" color="text" fontWeight="light">
                    Try adjusting your search.
                  </MDTypography>
                </MDBox>
              </Card>
            )}

            {filteredContacts.length > 0 && (
              <Card>
                <MDBox
                  px={2}
                  py={1}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <MDBox display="flex" alignItems="center">
                    <Checkbox
                      size="small"
                      checked={allFilteredSelected}
                      indeterminate={someFilteredSelected}
                      onChange={toggleSelectAll}
                    />

                    <MDTypography variant="button" color="text">
                      Select all
                    </MDTypography>
                  </MDBox>

                  <MDTypography variant="caption" color="text">
                    {filteredContacts.length} contact
                    {filteredContacts.length === 1 ? "" : "s"}
                  </MDTypography>
                </MDBox>
              </Card>
            )}

            {filteredContacts.map((row) => (
              <Card
                key={row.reference}
                sx={{
                  marginTop: 0,
                }}
              >
                <MDBox p={2}>
                  <MDBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    gap={2}
                  >
                    <MDBox display="flex" alignItems="flex-start" flex={1}>
                      <Checkbox
                        size="small"
                        sx={{
                          mt: -0.5,
                          ml: -1,
                          mr: 0.5,
                        }}
                        checked={selectedContacts.includes(row.reference)}
                        onChange={() => toggleContactSelection(row.reference)}
                      />

                      <MDBox flex={1}>
                        <MDTypography variant="h6" fontWeight="medium">
                          {row.name}
                        </MDTypography>

                        <MDTypography variant="button" color="text" fontWeight="light">
                          {row.phone_number}
                        </MDTypography>
                      </MDBox>
                    </MDBox>

                    <Chip
                      label={row.called ? "Called" : "Pending"}
                      color={row.called ? "success" : "default"}
                      size="small"
                    />
                  </MDBox>

                  {/* Tag */}
                  <MDBox mt={2}>
                    <Chip label={row.tag?.title || "---"} size="small" variant="outlined" />
                  </MDBox>

                  {/* Attributes */}
                  {row.attributes && Object.keys(row.attributes).length > 0 && (
                    <MDBox mt={2} display="flex" flexDirection="column" gap={1}>
                      {Object.entries(row.attributes).map(([key, value]) => (
                        <MDBox key={key} display="flex" justifyContent="space-between" gap={2}>
                          <MDTypography variant="caption" color="text" fontWeight="medium">
                            {formatAttributeName(key)}
                          </MDTypography>

                          <MDTypography variant="caption" color="text" textAlign="right">
                            {formatAttributeValue(value)}
                          </MDTypography>
                        </MDBox>
                      ))}
                    </MDBox>
                  )}

                  {/* Actions */}
                  <MDBox mt={2} display="flex" justifyContent="space-between" alignItems="center">
                    <MDButton
                      size="small"
                      color="dark"
                      variant="text"
                      startIcon={<Icon>edit</Icon>}
                      onClick={() => openEditDialog(row)}
                    >
                      Edit
                    </MDButton>

                    <MDButton
                      size="small"
                      color="error"
                      variant="text"
                      startIcon={<Icon>delete</Icon>}
                      onClick={() => deleteContact(row.reference)}
                    >
                      Delete
                    </MDButton>
                  </MDBox>
                </MDBox>
              </Card>
            ))}
          </MDBox>
        ) : (
          /* DESKTOP */
          <Card
            sx={{
              marginTop: 0,
            }}
          >
            <MDBox p={0}>
              <DataTable
                table={{
                  columns,
                  rows: filteredContacts,
                }}
                isSorted={true}
                entriesPerPage={{
                  defaultValue: 10,
                  entries: [50, 100, 500, 1000],
                }}
                showTotalEntries={true}
                noEndBorder
                canSearch={false}
              />
            </MDBox>
          </Card>
        )}
      </MDBox>

      <Footer />
    </DashboardLayout>
  );
}

export default CallCenterContactsPage;
